import { Component, Input, OnInit, OnChanges, AfterViewInit, OnDestroy, forwardRef } from '@angular/core';
import { Output, EventEmitter } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, } from "@angular/forms";
import { ModalController, NavParams } from '@ionic/angular';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { AppConstants } from '@app/app.constants';
import { languageSupport, languageService } from '@app/modules/common/language';
import { viewService } from '@app/modules/view';
import { httpService } from '@app/modules/common/http';

@Component({
    selector: 'modal-crop-image',
    templateUrl:'./ui-modal.html',
    styleUrls: [ 
        './ui-modal.scss'
    ]
})
export class UiModalCrop extends languageSupport implements OnInit, OnDestroy {
    public _image = null;
    public _height = 0;
    public _width = 0;
    public _loaded = false;

    constructor(private lang: languageService, private modalCtrl: ModalController, private params: NavParams, public view: viewService) {
        super(lang, null);
    }

    ngOnInit() {
        this._image = this.params.get('image');
        this._height = this.params.get('height');
        this._width = this.params.get('width');
    }    

    ngOnDestroy(){
        super.OnDestroy();
    }

    async OnCrop(data){
        await this.modalCtrl.dismiss(data);
    }
    
    async CloseModal() {
        await this.modalCtrl.dismiss(null);
    }        
}

@Component({
    selector: 'ui-image',
    templateUrl:'./ui-image.html',
    styleUrls: [ 
        './ui-image.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UiImage),
            multi: true
        }            
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiImage extends languageSupport implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input('readonly') _readonly = true;
    @Input('src') _src = null;
    @Input('crheight') _croppedHeight = '0';
    @Input('crwidth') _croppedWidth = '0';
    
    // Eager intentional: SW cache-first (ngsw-config.json) makes lazy
    // a no-op for bandwidth, and lazy waits on IntersectionObserver →
    // empty-frame flash between mesa views.
    public _loading = 'eager';

    private _onChangeCallback: any = () => {}
    private _onTouchCallback: any = () => {}
    
    @ViewChild('image', {static: false}) _image: ElementRef;

    get _cors(){    // use crossorigin for server images
        return !this._src.startsWith('http') || (this._src && (this._src.startsWith(AppConstants.baseURL)));
    }

    private _b64 = null;
    private get _value(){
        return this._b64;
    }

    private set _value(value){
        // Ruta del ControlValueAccessor (writeValue) y del recorte: cambia la
        // fuente SIN pasar por ngOnChanges, asi que debe usar la MISMA ruta de
        // invalidacion/reset/confirmacion; sin esto un decode en vuelo del src
        // anterior podia marcar la URL nueva, y un valor asignado tras
        // ngAfterViewInit no programaba la confirmacion (se perdia un load de
        // cache -> gris).
        const _changed = (this._src !== value);
        this._b64 = value;
        this._src = value;
        if (_changed){
            this._onSrcChanged();
        }
        this.change.markForCheck();
    }

    // Bind _src straight: SW cache-first (ngsw-config.json) + browser
    // decoded-image cache handles repeats.
    get _source(){
        return this._value || this._src;
    }

    private _c64: any = null;
    get Base64() {
        return new Promise((resolve) =>{
            if (!this._c64){
                let _img = new Image();
                _img.onload = () => {
                    let _cnv = document.createElement("canvas");
                    var _ctx = _cnv.getContext("2d");
        
                    _cnv.width = _img.naturalWidth;
                    _cnv.height = _img.naturalHeight;
                    _ctx.drawImage(_img, 0, 0);

                    let _dataurl = _cnv.toDataURL("image/png");
                    this._c64 = _dataurl.split(',', 2)[1];

                    resolve(this._c64);  
                }
    
                _img.crossOrigin = "Anonymous";
                _img.src = this._source;
            }
            else {
                resolve(this._c64);    
            }
        });
    }

    constructor(private lang: languageService, private change: ChangeDetectorRef, private modalCtrl: ModalController, private http: httpService){
        super(lang, change);
    }

    public _loaded = false;
    public _online = true;

    // Per-session memory of URLs that finished decoding at least once.
    // Mounting a new <ui-image> for a URL already in this set starts with
    // _loaded=true so the browser/SW cache can serve the bytes synchronously
    // without flashing the spinner — without this, navigating back and forth
    // in the product grid shows the loading state on every entry even though
    // the image is already cached. data: URIs are not tracked (they would
    // grow the set unbounded and load instantly anyway).
    private static _loadedSrcs = new Set<string>();

    // Persistencia de _loadedSrcs entre recargas, keyed por version de la app
    // (mismo patron que el ?v=Version de los JSON de idioma). Sin esto el Set
    // estatico se vacia en cada recarga y las imagenes ya cacheadas por el SW
    // vuelven a parpadear en gris hasta re-detectar la carga. Con esto, tras
    // recargar, las URLs ya vistas arrancan con _loaded=true -> instantaneo,
    // "aunque recargue ya esta". Keyed por version: tras un deploy el SW
    // reemplaza el cache, asi que el set se resetea (primera vista en gris
    // como hoy, sin parpadeo a blanco de bytes que ya no estan cacheados).
    // Key fija; la version va DENTRO del valor ({ v, urls }) para resetear por
    // version sin escanear/borrar otras keys de localStorage.
    private static _persistKey = 'uiimage_loaded';
    private static _persistHydrated = false;
    private static _persistDirty = false;
    private static _persistTimer: any = null;
    private static _flushBound = false;
    // Tras el 1er fallo de storage (cuota/indisponible) dejamos de reintentar
    // el resto de la sesion: evita relanzar setItem costoso en cada rafaga.
    private static _persistDisabled = false;
    // Cap de URLs persistidas: el catalogo es finito, pero acotamos por si el
    // set creciera (evita que JSON.stringify/setItem se encarezca sin limite).
    private static readonly _persistMax = 1000;

    private static _hydratePersisted(){
        if (UiImage._persistHydrated){
            return;
        }
        UiImage._persistHydrated = true;
        UiImage._bindFlush();   // flush inmediato al ocultar/recargar la pagina
        try {
            // Una sola getItem + version-check: si la version no coincide
            // (deploy nuevo) se ignora y se sobrescribe en el proximo write.
            // Sin escaneo de localStorage.length -> sin jank al montar.
            const _raw = localStorage.getItem(UiImage._persistKey);
            if (_raw){
                const _obj = JSON.parse(_raw);
                if (_obj && _obj.v === AppConstants.Version && Array.isArray(_obj.urls)){
                    // Cap al hidratar (mismo _persistMax) + filtro data: para no
                    // inflar el Set ni hacer trabajo sincrono de mas si la lista
                    // guardada es enorme o esta corrupta.
                    let _urls = _obj.urls;
                    if (_urls.length > UiImage._persistMax){
                        _urls = _urls.slice(-UiImage._persistMax);
                    }
                    for (const _u of _urls){
                        if (typeof _u === 'string' && !_u.startsWith('data:')){
                            UiImage._loadedSrcs.add(_u);
                        }
                    }
                }
            }
        } catch(_){
            // localStorage no disponible / JSON corrupto / cuota: seguimos solo
            // en memoria (comportamiento previo, sin persistencia).
        }
    }

    // Escribe el set a localStorage ya (sin esperar al debounce). Ignora
    // fallos de cuota / no disponible: seguimos en memoria.
    private static _writePersist(){
        if (UiImage._persistTimer != null){
            clearTimeout(UiImage._persistTimer);
            UiImage._persistTimer = null;
        }
        if (UiImage._persistDisabled || !UiImage._persistDirty){
            return;
        }
        UiImage._persistDirty = false;
        try {
            // Cap a las _persistMax mas recientes (Set conserva orden de insercion).
            let _arr = Array.from(UiImage._loadedSrcs);
            if (_arr.length > UiImage._persistMax){
                _arr = _arr.slice(-UiImage._persistMax);
            }
            localStorage.setItem(UiImage._persistKey, JSON.stringify({ v: AppConstants.Version, urls: _arr }));
        } catch(_){
            // Cuota excedida / no disponible: deshabilitamos la persistencia el
            // resto de la sesion para no reintentar setItem (costoso) en cada
            // rafaga. Seguimos en memoria (comportamiento previo).
            UiImage._persistDisabled = true;
        }
    }

    private static _persistLoadedSrcs(){
        if (UiImage._persistDisabled){
            return;   // storage fallo antes: no reintentamos esta sesion
        }
        UiImage._persistDirty = true;
        if (UiImage._persistTimer != null){
            return;
        }
        // Escritura debounced (en lote): no tocamos localStorage por cada
        // imagen (en datafono seria costoso) — un unico setItem por rafaga.
        UiImage._persistTimer = setTimeout(() => UiImage._writePersist(), 2000);
    }

    // Flush inmediato antes de que la pagina se oculte/recargue: sin esto, lo
    // recien cargado (aun en la ventana de 2s del debounce) se perderia si el
    // usuario recarga justo tras estrenar una familia. Se engancha una sola
    // vez, a nivel window/document (no por componente).
    private static _bindFlush(){
        if (UiImage._flushBound){
            return;
        }
        UiImage._flushBound = true;
        try {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden'){
                    UiImage._writePersist();
                }
            });
            window.addEventListener('pagehide', () => UiImage._writePersist());
        } catch(_){
            // sin DOM (SSR/tests) o addEventListener no disponible: seguimos
            // solo con el debounce.
        }
    }

    private _decodeWatchId: number = null;
    // Invalida un decode() en vuelo cuando _src cambia a mitad: sin esto, un
    // decode viejo podria resolver y marcar _loaded para una URL que ya no es
    // la actual.
    private _decodeToken = 0;
    // Barrera para callbacks async (decode()) que resuelven tras destruir el
    // componente: sin esto llamarian OnLoaded()/markForCheck() sobre una vista
    // muerta.
    private _destroyed = false;

    private _stopDecodeWatch(){
        if (this._decodeWatchId != null){
            cancelAnimationFrame(this._decodeWatchId);
            this._decodeWatchId = null;
        }
    }

    // Confirmacion fiable de "imagen lista para pintar". El evento (load) del
    // template se puede PERDER cuando montan muchas <ui-image> a la vez (p.ej.
    // un producto con muchas opciones): la imagen la sirve la cache del SW/
    // navegador y decodifica ANTES de que Angular enganche el listener, asi que
    // (load) nunca llega y el placeholder gris se queda fijo hasta salir y
    // volver a entrar (entonces complete=true al instante). HTMLImageElement
    // .decode() resuelve cuando la imagen esta decodificada, independiente de
    // cazar (load) en el momento justo — es la via robusta para este caso.
    private _confirmLoaded(){
        // El setTimeout de ngOnChanges puede dispararse tras ngOnDestroy: no
        // programar decode/rAF ni tocar la vista si ya se destruyo.
        if (this._destroyed){
            return;
        }
        this._stopDecodeWatch();
        const _el: HTMLImageElement = this._image?.nativeElement;
        if (!_el){
            return;
        }
        // Ya decodificada (cache sincrona): marcar sin esperar a nada.
        if (_el.complete && _el.naturalWidth > 0){
            this.OnLoaded();
            return;
        }
        const _token = ++this._decodeToken;
        if (typeof _el.decode === 'function'){
            _el.decode().then(() => {
                // decode() no se puede cancelar: si el componente ya se destruyo
                // (o _src cambio, o ya se marco) no tocamos OnLoaded/markForCheck
                // sobre una vista muerta. _destroyed sube en ngOnDestroy.
                if (!this._destroyed && _token === this._decodeToken && !this._loaded){
                    this.OnLoaded();
                }
            }).catch(() => {
                // decode() rechaza si el src cambia a mitad (EncodingError) o si
                // la imagen falla: el (load)/(error) del template cubre esos
                // casos y el poll rAF queda de red de seguridad.
            });
        }
        // Red de seguridad final (navegadores sin decode(), o decode que no
        // resuelve): poll corto por rAF, igual que antes.
        this._scheduleDecodeWatch();
    }

    // Ruta UNICA de cambio de fuente, compartida por ngOnChanges (Input) y por
    // el _value setter (writeValue del CVA / recorte). Debe llamarse DESPUES de
    // fijar this._src al nuevo valor. Invalida cualquier decode en vuelo del src
    // anterior, resetea el estado de carga y programa la confirmacion por
    // decode()/complete (no solo el evento (load), que se pierde con bytes de
    // cache). El setTimeout(0) espera a que Angular monte el nuevo <img>.
    private _onSrcChanged(){
        this._decodeToken++;
        this._c64 = null;
        this._loaded = this._online && this._isCachedSrc(this._src);
        setTimeout(() => this._confirmLoaded(), 0);
    }

    private _scheduleDecodeWatch(){
        this._stopDecodeWatch();
        let _attempts = 0;
        const _tick = () => {
            this._decodeWatchId = null;
            if (this._loaded){
                return;
            }
            const _el = this._image?.nativeElement;
            if (_el?.complete && _el.naturalWidth > 0){
                this.OnLoaded();
                return;
            }
            if (++_attempts < 90){
                this._decodeWatchId = requestAnimationFrame(_tick);
            }
        };
        this._decodeWatchId = requestAnimationFrame(_tick);
    }

    private _isCachedSrc(src: string): boolean {
        if (!src || src.startsWith('data:')){
            return false;   // no candidato a cache: no tocamos persistencia
        }
        UiImage._hydratePersisted();   // rellena el set desde localStorage la 1a vez
        return UiImage._loadedSrcs.has(src);
    }

    private _markSrcCached(src: string){
        if (src && !src.startsWith('data:')){
            // Hidratar ANTES de escribir: en arranque offline _isCachedSrc no
            // corre (this._online && ... hace short-circuit), y sin esto el
            // primer write pisaria el historico persistido con solo las URLs
            // de esta sesion, rompiendo la persistencia entre recargas.
            UiImage._hydratePersisted();
            UiImage._loadedSrcs.add(src);
            UiImage._persistLoadedSrcs();
        }
    }

    get IsVisible(){
        return this._loaded;
    }

    OnLoaded(){
        // Punto de embudo de todas las vias (decode, rAF, setTimeout, (load)):
        // si ya se destruyo el componente no tocamos markForCheck sobre una
        // vista muerta.
        if (this._destroyed){
            return;
        }
        this._loaded = true;
        this._markSrcCached(this._src);
        this.change.markForCheck();
    }

    // The template no longer gates the <img> by _online — the Service
    // Worker is cache-first on /upload/* and /avatar/*, so the tag must
    // mount even when the app starts offline so cached bytes can render.
    // When we come back online and the previous fetch had failed
    // (_loaded still false) we re-issue the request by reassigning .src
    // on the native element: that triggers a fresh fetch which the SW
    // can now satisfy from network or refreshed cache.
    set Online(value){
        let _wasOffline = !this._online;
        this._online = value;

        if (value && _wasOffline && !this._loaded && this._source){
            setTimeout(() => {
                if (this._destroyed || this._loaded){
                    return;
                }
                if (this._image && this._image.nativeElement){
                    this._image.nativeElement.src = this._source;
                    // No basta con esperar el (load) (se pierde si la cache del
                    // SW sirve los bytes y decodifica antes de reengancharlo):
                    // confirmamos por decode()/complete como en los demas paths.
                    this._confirmLoaded();
                }
            }, 0);
        }

        this.change.markForCheck();
    }

    private _online_subscription = null;
    ngOnInit() {
        // Subscribe unconditionally: the component may start online and
        // later go offline, in which case we still need to update _online
        // so a subsequent _src change is gated correctly.
        this._online = this.http.IsOnline;
        this._online_subscription = this.http.OnOnline.subscribe(
        data => {
            this.Online = data;
        });

        // Skip the spinner on remount if this URL already finished decoding
        // earlier in the session — the browser/SW cache will serve it
        // synchronously and (load) still fires to confirm.
        // Offline: NO asumimos cache disponible — el SW puede no tenerla, la
        // imagen no carga y queda gris sin placeholder. Mejor dejar _loaded
        // en false para que se vea el gif de beer-loading hasta que el
        // (load) real confirme.
        if (this._online && this._isCachedSrc(this._src)){
            this._loaded = true;
        }
    }

    ngAfterViewInit(){
        // Race con el cache del navegador: si el <img> se monta con una
        // URL ya en cache (o en localStorage del SW), la imagen puede
        // decodificarse ANTES de que Angular monte el listener (load).
        // En ordenadores lentos pasa mucho al cambiar de familia: 15+
        // ui-image nuevos a la vez y solo el primero dispara (load).
        // decode() confirma la decodificacion sin depender de cazar (load);
        // el poll rAF queda de red de seguridad dentro de _confirmLoaded.
        this._confirmLoaded();
    }

    // Handler defensivo para el (error) del <img>. Sin este handler, una
    // imagen que falla (404, red caida) deja _loaded=false → gris eterno.
    // Marcamos loaded para al menos ocultar el placeholder — el usuario
    // vera el icono de imagen rota del navegador, que es mejor que un
    // gris silencioso permanente.
    OnError(){
        this._loaded = true;
        this.change.markForCheck();
    }

    ngOnChanges(changes){
        // Reset _loaded solo cuando _src cambia a un valor DISTINTO. Angular
        // puede disparar ngOnChanges con changes['_src'] existente pero mismo
        // currentValue === previousValue en varios escenarios (rebindings del
        // padre en CD ciclos, parents con Inputs no-inmutables). Sin este
        // guard, imagenes ya pintadas parpadeaban a gris cuando el padre
        // re-renderiza sin que la URL cambiara realmente. Reportado por
        // usuario: "imagenes que estaban pintadas desaparecen".
        //
        // Cuando _src si cambia:
        // - Nueva URL en _loadedSrcs (ya decodificada antes) → skip spinner.
        // - Nueva URL sin ver → _loaded=false → spinner mientras carga.
        if (!changes){
            this._loaded = this._online && this._isCachedSrc(this._src);
        }
        else if (changes['_src']){
            const _prev = changes['_src'].previousValue;
            const _curr = changes['_src'].currentValue;
            if (_prev !== _curr){
                // Misma ruta de invalidacion/reset/confirmacion que el CVA. El
                // ++ del token es sincrono (antes del setTimeout de
                // _confirmLoaded), asi un decode del src viejo no marca la URL
                // nueva en esa ventana.
                this._b64 = null;
                this._onSrcChanged();
            }
            // Si _prev === _curr, preserva _loaded y las caches — es un
            // re-emit espurio del padre, no un cambio real.
            return;
        }
        this._c64 = null;
        this._b64 = null;
    }

    ngOnDestroy(){
        super.OnDestroy();
        // Marca destruido e invalida cualquier decode() en vuelo (el token deja
        // de coincidir) para que su .then no toque la vista muerta.
        this._destroyed = true;
        this._decodeToken++;
        this._stopDecodeWatch();

        if (this._online_subscription){
            this._online_subscription.unsubscribe();
            this._online_subscription = null;
        }
    }

    private _showCropModal(_imagesrc){
        return new Promise(async (resolve) => {
            let _image = new Image();
            _image.src = _imagesrc;
            
            const modal = await this.modalCtrl.create({ 
                component: UiModalCrop,
                componentProps: { 
                    image: _image,
                    height: Number(this._croppedHeight),
                    width: Number(this._croppedWidth),
                }
            });
            
            modal.onDidDismiss().then((response) => {
                if (response && response.data){
                    this.value = response.data;
                }
                
                resolve(true);
            });        
                    
            await modal.present();                
        });
    }
    
    @Output('onChange') _onChange = new EventEmitter<any>();         
    OnChange(event){
        return new Promise((resolve) => {
            let self = this;
            this._readImage(event.target.files[0])
            .then((dataURL) => {
                self._showCropModal(dataURL).then(
                data => {
                    resolve(true);
                });
            });
            
            event.srcElement.value = null;        
            this._onChange.emit();    
        });
    }
    
    /************************************************/
    /* adjust image according to exif information   */
    /************************************************/

    // https://stackoverflow.com/questions/7584794/accessing-jpeg-exif-rotation-data-in-javascript-on-the-client-side/32490603#32490603
    private _getOrientation(event) {
        var view = new DataView(event.target.result);
        if (view.getUint16(0, false) != 0xFFD8)
        {
            -2; // not a JPEG
        }
        var length = view.byteLength, offset = 2;
        while (offset < length) 
        {
            if (view.getUint16(offset+2, false) <= 8){
                return -1;  // not defined
            }
            
            var marker = view.getUint16(offset, false);
            offset += 2;
            if (marker == 0xFFE1) 
            {
                if (view.getUint32(offset += 2, false) != 0x45786966) 
                {
                    return -1;  // not defined
                }

                var little = view.getUint16(offset += 6, false) == 0x4949;
                offset += view.getUint32(offset + 4, little);
                var tags = view.getUint16(offset, little);
                offset += 2;
                for (var i = 0; i < tags; i++)
                {
                    if (view.getUint16(offset + (i * 12), little) == 0x0112)
                    {
                        return view.getUint16(offset + (i * 12) + 8, little);
                    }
                }
            }
            else if ((marker & 0xFF00) != 0xFF00)
            {
                break;
            }
            else
            { 
                offset += view.getUint16(offset, false);
            }
        }
        
        return -1;  // not defined
    };

    // https://github.com/mshibl/Exif-Stripper/blob/master/exif-stripper.js
    private _removeExif(event){
        var imageArrayBuffer = event.target.result;
        var dv = new DataView(imageArrayBuffer);
        
	    var offset = 0, recess = 0;
	    var pieces = [];
	    var i = 0;
	    if (dv.getUint16(offset) == 0xffd8){
	        offset += 2;
	        var app1 = dv.getUint16(offset);
	        offset += 2;
	        while (offset < dv.byteLength){
	            if (app1 == 0xffe1){
	                pieces[i] = {recess:recess,offset:offset-2};
	                recess = offset + dv.getUint16(offset);
	                i++;
	            }
	            else if (app1 == 0xffda){
	                break;
	            }
	            offset += dv.getUint16(offset);
	            var app1 = dv.getUint16(offset);
	            offset += 2;
	        }
	        if (pieces.length > 0){
	            var newPieces = [];
	            pieces.forEach(function(v){
	                newPieces.push(imageArrayBuffer.slice(v.recess, v.offset));
	            }, this);
	            newPieces.push(imageArrayBuffer.slice(recess));
                
                return new Blob(newPieces, {type: 'image/jpeg'});
	        }
	    }
	}    
    
    // https://stackoverflow.com/questions/20600800/js-client-side-exif-orientation-rotate-and-mirror-jpeg-images    
    private _imgTransform(callback, orientation, image, type){
        let canvas = document.createElement('canvas');

        let w = image.width
        let h = image.height; 

        let context = canvas.getContext('2d');            
        
        let max = 1024, scale = 1; // max side: 1024 pixels
        if ((w > h) && (w > max)){
            scale = max/w;
        }
        if ((h >= w) && (h > max)){
            scale = max/h;
        }

        w = Math.round(w * scale);
        h = Math.round(h * scale);
    
        if ((orientation > 4) && (orientation < 9)) {
            canvas.width = h;
            canvas.height = w;
        } 
        else {
            canvas.width = w;
            canvas.height = h;
        }
        
        let tm = [ 1, 0, 0, 1, 0, 0 ];  // transformation matrix        
        switch (orientation) {
            case 2: tm = [ -1, 0, 0, 0, image.width, 0 ]; break;
            case 3: tm = [ -1, 0, 0, -1, image.width, image.height ]; break;
            case 4: tm = [ 1, 0, 0, -1, 0, image.height ]; break;
            case 5: tm = [ 0, 1, 1, 0, 0, 0 ]; break;
            case 6: tm = [ 0, 1, -1, 0, image.height, 0 ]; break;
            case 7: tm = [ 0, -1, -1, 0, image.height, image.width ]; break;
            case 8: tm = [ 0, -1, 1, 0, 0, image.width ]; break;
        }
        
        context.transform(scale, 0, 0, scale, 0, 0);
        context.transform(tm[0], tm[1], tm[2], tm[3], tm[4], tm[5]);        
        
        context.drawImage(image, 0, 0);
        callback(canvas.toDataURL());
    }
        
    private _readImage(file) {
        let promise = new Promise <any> ((resolve) => {
            const arrReader = new FileReader();
            arrReader.onload = (event) => {
                let _blob = this._removeExif(event);
                if (_blob){
                    const image = new Image();
                    image.onload = () => {
                        this._imgTransform((dataURL) => {
                            resolve(dataURL);
                        }, this._getOrientation(event), image, file.type);                        
                    }
                    image.src = URL.createObjectURL(_blob);
                }
                else {  // no exif information ?? 
                    const imgReader = new FileReader();
                    imgReader.onload = (event) => {
                        const image = new Image();
                        image.onload = () => {
                            this._imgTransform((dataURL) => {
                                resolve(dataURL);
                            }, -1, image, file.type);                        
                        }
                        image.src = imgReader.result as string;
                    };
                    imgReader.readAsDataURL(file);
                }
            }
            arrReader.readAsArrayBuffer(file);            
        });
        
        return promise;
    }    
    
    /********************************************/
    /* get/set accesors                         */
    /********************************************/
    
    get value(): any {
        return this._value || this._src;
    }

    set value(v: any) {
        if (v !== this._src) {
            this._value = v;

            this._onChangeCallback(v);
            this._onTouchCallback(v);

            this._onChange.emit();
        }
    }    
    
    /********************************************/
    /* ControlValueAccessor                     */
    /********************************************/
    
    writeValue(value: any) {
        if (value !== this._src) {
            this.value = value;
        }
    }

    registerOnChange(fn: any) {
        this._onChangeCallback = fn;
    }

    registerOnTouched(fn: any) {
        this._onTouchCallback = fn;
    } 
};
